import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Typography,
  Box,
  Button,
  Paper,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import ClearIcon from "@mui/icons-material/Clear";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SpeedIcon from "@mui/icons-material/Speed";
import BlockIcon from "@mui/icons-material/Block";
import AssignmentService from "../services/AssignmentService";
import type { Assignment } from "../services/AssignmentService";
import { useSnackbar } from "notistack";
import SignatureCanvas from "react-signature-canvas";
import axios from "axios";
import { API_URL } from "../utils/api-config";

interface SignaturesPadRefs {
  manager: React.RefObject<SignatureCanvas | null>;
  conducteur: React.RefObject<SignatureCanvas | null>;
}

interface SignaturePreviewState {
  open: boolean;
  title: string;
  signature: string | null;
}

const Signatures = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isReturn = searchParams.get("return") === "true";
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [managerSigned, setManagerSigned] = useState(false);
  const [conducteurSigned, setConducteurSigned] = useState(false);
  const [usageInterne, setUsageInterne] = useState(false); // Mode usage interne - skip signatures

  // État pour gérer la prévisualisation des signatures
  const [previewState, setPreviewState] = useState<SignaturePreviewState>({
    open: false,
    title: "",
    signature: null,
  });

  // État pour la boîte de dialogue de confirmation
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // #50 — Clôturer sans signature
  const [closeNoSigOpen, setCloseNoSigOpen] = useState(false);
  const [closeNoSigReason, setCloseNoSigReason] = useState<string>("");
  const [closeNoSigLoading, setCloseNoSigLoading] = useState(false);

  // Références aux Canvas de signature
  const signaturePadRefs: SignaturesPadRefs = {
    manager: useRef<SignatureCanvas | null>(null),
    conducteur: useRef<SignatureCanvas | null>(null),
  };

  // Charger les détails de l'affectation
  useEffect(() => {
    const fetchAssignment = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await AssignmentService.getById(id);
        console.log("[Signatures] Affectation récupérée:", data);

        // Vérifier l'étape actuelle
        const expectedStep = isReturn
          ? "signatures_retour"
          : "signatures_entree";

        // Si l'étape est déjà passée à inspection_exterieure_retour après la checklist,
        // cela signifie que nous avons un cas particulier où le flux de processus a été modifié par l'API
        const isReturnStarted =
          !isReturn && data.currentStep === "inspection_exterieure_retour";

        // Si l'étape est "debriefing", cela signifie que le processus est déjà en fin de cycle
        const isDebriefingStep = data.currentStep === "debriefing";

        if (
          data.currentStep !== expectedStep &&
          !isReturnStarted &&
          !isDebriefingStep
        ) {
          console.log(
            `[Signatures] L'étape actuelle (${data.currentStep}) ne correspond pas à celle attendue (${expectedStep})`
          );

          // Si nous sommes dans la phase de retour mais à une étape précédente, tenter le passage à signatures_retour
          if (isReturn && (data.currentStep === "checklist_retour" || data.currentStep === "inventaire_documents_retour")) {
            console.log(
              "[Signatures] Tentative de passage à l'étape signatures_retour"
            );
            try {
              const updatedAssignment = await AssignmentService.nextStep(id);
              setAssignment(updatedAssignment);

              if (updatedAssignment.currentStep !== "signatures_retour") {
                setError(
                  "Impossible d'atteindre l'étape de signatures (retour)"
                );
              }
            } catch (syncError) {
              console.error(
                "[Signatures] Erreur lors de la synchronisation des étapes:",
                syncError
              );
              setError(
                "Impossible de synchroniser l'étape avec la page actuelle"
              );
            }
          }
          // Si nous ne sommes pas dans la phase de retour mais à une étape précédente, tenter le passage à signatures_entree
          else if (!isReturn && (data.currentStep === "checklist" || data.currentStep === "inventaire_documents")) {
            console.log(
              "[Signatures] Tentative de passage à l'étape signatures_entree"
            );
            try {
              const updatedAssignment = await AssignmentService.nextStep(id);
              setAssignment(updatedAssignment);

              if (updatedAssignment.currentStep !== "signatures_entree") {
                setError("Impossible d'atteindre l'étape de signatures");
              }
            } catch (syncError) {
              console.error(
                "[Signatures] Erreur lors de la synchronisation des étapes:",
                syncError
              );
              setError(
                "Impossible de synchroniser l'étape avec la page actuelle"
              );
            }
          } else if (isReturnStarted) {
            // Si l'API a déjà avancé l'étape à inspection_exterieure_retour, on affiche un message mais on continue
            console.log(
              "[Signatures] L'étape est déjà avancée au processus de retour. Signatures toujours possibles."
            );
            setAssignment(data);
            enqueueSnackbar(
              "Le processus de retour a déjà commencé. Vous pouvez compléter les signatures d'entrée avant de continuer.",
              {
                variant: "info",
              }
            );
            setError(null);
          } else {
            // Si nous sommes dans une autre étape non compatible
            setAssignment(data);
            setError(
              `L'étape actuelle (${
                data.currentStep
              }) ne correspond pas à la signature ${
                isReturn ? "de retour" : "d'entrée"
              }`
            );
          }
        } else {
          // L'étape est correcte, initiale, de retour, ou de debriefing
          setAssignment(data);

          // Si on est déjà à l'étape de débriefing
          if (isDebriefingStep) {
            console.log(
              "[Signatures] L'affectation est déjà au stade de débriefing"
            );
            enqueueSnackbar(
              "Cette affectation est déjà en phase de finalisation",
              {
                variant: "info",
              }
            );
          }
        }

        // Charger les signatures existantes s'il y en a
        loadExistingSignatures(data);
      } catch (err) {
        console.error(
          "[Signatures] Erreur lors du chargement de l'affectation:",
          err
        );
        setError("Impossible de charger les détails de l'affectation");
        enqueueSnackbar("Erreur lors du chargement de l'affectation", {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAssignment();
  }, [id, enqueueSnackbar, isReturn]);

  // Charger les signatures existantes
  const loadExistingSignatures = (data: Assignment) => {
    if (!data) return;

    const signatures = isReturn ? data.returnSignatures : data.signatures;
    const hasManagerSignature = signatures?.manager ? true : false;
    const hasConducteurSignature = signatures?.conducteur ? true : false;

    // Mettre à jour l'état des signatures complétées
    setManagerSigned(hasManagerSignature);
    setConducteurSigned(hasConducteurSignature);

    // Si des signatures existent, les afficher dans le canvas
    if (hasManagerSignature && signaturePadRefs.manager.current) {
      const img = new Image();
      img.onload = () => {
        const canvas = signaturePadRefs.manager.current;
        if (canvas) {
          canvas.clear();
          const ctx = canvas.getCanvas().getContext("2d");
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            setManagerSigned(true);
          }
        }
      };
      img.src = signatures!.manager!;
    }

    if (hasConducteurSignature && signaturePadRefs.conducteur.current) {
      const img = new Image();
      img.onload = () => {
        const canvas = signaturePadRefs.conducteur.current;
        if (canvas) {
          canvas.clear();
          const ctx = canvas.getCanvas().getContext("2d");
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            setConducteurSigned(true);
          }
        }
      };
      img.src = signatures!.conducteur!;
    }
  };

  // Réinitialiser une signature
  const clearSignature = (type: keyof SignaturesPadRefs) => {
    if (signaturePadRefs[type].current) {
      signaturePadRefs[type].current?.clear();

      if (type === "manager") {
        setManagerSigned(false);
      } else if (type === "conducteur") {
        setConducteurSigned(false);
      }
    }
  };

  // Vérifier si les signatures sont complètes
  const areSignaturesComplete = () => {
    // Si usage interne, pas besoin de signatures
    if (usageInterne) return true;
    return (
      !signaturePadRefs.manager.current?.isEmpty() &&
      !signaturePadRefs.conducteur.current?.isEmpty()
    );
  };

  // Gérer l'événement de fin de signature
  const handleEndDrawing = (type: keyof SignaturesPadRefs) => {
    if (type === "manager") {
      setManagerSigned(!signaturePadRefs.manager.current?.isEmpty());
    } else if (type === "conducteur") {
      setConducteurSigned(!signaturePadRefs.conducteur.current?.isEmpty());
    }
  };

  // Ouvrir la prévisualisation d'une signature
  const handlePreviewSignature = (type: keyof SignaturesPadRefs) => {
    if (
      !signaturePadRefs[type].current ||
      signaturePadRefs[type].current?.isEmpty()
    ) {
      enqueueSnackbar("Aucune signature à prévisualiser", {
        variant: "warning",
      });
      return;
    }

    const title =
      type === "manager"
        ? "Signature du gestionnaire"
        : "Signature du conducteur";
    const signature = signaturePadRefs[type].current?.toDataURL();

    setPreviewState({
      open: true,
      title,
      signature,
    });
  };

  // Fermer la prévisualisation
  const handleClosePreview = () => {
    setPreviewState({
      ...previewState,
      open: false,
    });
  };

  // Soumettre les signatures
  const handleSubmit = async () => {
    if (!id) return;
    if (!areSignaturesComplete()) {
      enqueueSnackbar("Veuillez signer tous les champs requis", {
        variant: "warning",
      });
      return;
    }

    // Ouvrir la boîte de dialogue de confirmation au lieu de soumettre directement
    setConfirmDialogOpen(true);
  };

  // Confirmer et soumettre les signatures
  const handleConfirmSubmit = async () => {
    if (!id) return;

    try {
      setSubmitting(true);
      setConfirmDialogOpen(false);
      console.log("[Signatures] Début du processus de soumission");

      // Récupérer les signatures en base64 (vide si usage interne)
      const signaturesData = {
        signatures: {
          manager: usageInterne ? "usage_interne" : (signaturePadRefs.manager.current?.toDataURL() || ""),
          conducteur: usageInterne ? "usage_interne" : (signaturePadRefs.conducteur.current?.toDataURL() || ""),
        },
      };

      // Déterminer si nous sommes en mode retour
      // Si l'étape actuelle est inspection_exterieure_retour et qu'on n'est pas en mode retour,
      // cela signifie qu'on enregistre les signatures d'entrée après que l'API a avancé l'étape
      const isReturnMode =
        isReturn ||
        (assignment &&
          assignment.currentStep === "inspection_exterieure_retour");

      // Déterminer si on doit forcer la mise à jour des signatures
      // On force si l'étape actuelle n'est pas signatures_entree et qu'on n'est pas en mode retour
      const shouldForceUpdate =
        !isReturn &&
        assignment !== null &&
        assignment.currentStep !== "signatures_entree";

      console.log("[Signatures] État avant envoi:", {
        assignmentId: id,
        currentStep: assignment?.currentStep,
        isReturn,
        isReturnMode,
        shouldForceUpdate
      });
      console.log("[Signatures] Envoi des signatures");
      await AssignmentService.addSignatures(
        id,
        signaturesData,
        isReturn,
        shouldForceUpdate
      );

      // Une fois les signatures enregistrées, passer à l'étape suivante
      // sauf si nous sommes déjà en mode retour
      if (!isReturnMode) {
        console.log("[Signatures] Passage à l'étape suivante");
        const updatedAssignment = await AssignmentService.nextStep(id);
        setAssignment(updatedAssignment);
      }

      // Vérifier si des données RAS sont stockées (retour rapide)
      const rasDataStr = sessionStorage.getItem(`ras_end_data_${id}`);
      if (rasDataStr && isReturn) {
        console.log("[Signatures] Données RAS trouvées, finalisation de l'affectation");
        const rasData = JSON.parse(rasDataStr);
        const now = new Date();
        
        try {
          await AssignmentService.endAssignment(id, {
            endDate: now.toISOString().split('T')[0],
            endTime: now.toTimeString().slice(0, 5),
            endMileage: rasData.endMileage,
            endFuelLevel: rasData.endFuelLevel,
            comments: "RAS - Retour rapide sans anomalie"
          });
          
          // Nettoyer les données stockées
          sessionStorage.removeItem(`ras_end_data_${id}`);
          
          enqueueSnackbar("✅ Affectation terminée avec succès", {
            variant: "success",
          });
        } catch (endError) {
          console.error("[Signatures] Erreur lors de la finalisation:", endError);
          enqueueSnackbar("Signatures OK. Terminez l'affectation depuis le debriefing.", {
            variant: "warning",
          });
        }
      } else {
        enqueueSnackbar("Signatures enregistrées avec succès", {
          variant: "success",
        });
      }

      // Toujours rediriger vers la liste des affectations après les signatures
      navigate("/assignments");
    } catch (err) {
      console.error(
        "[Signatures] Erreur lors de l'enregistrement des signatures:",
        err
      );
      enqueueSnackbar("Erreur lors de l'enregistrement des signatures", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Annuler la soumission
  const handleCancelSubmit = () => {
    setConfirmDialogOpen(false);
  };

  // #50 — Clôturer sans signature
  const handleCloseWithoutSignature = async () => {
    if (!id || !closeNoSigReason) return;
    try {
      setCloseNoSigLoading(true);
      await axios.patch(`${API_URL}/assignments/${id}/close-without-signature`, {
        reason: closeNoSigReason,
      });
      enqueueSnackbar("Affectation cloturee sans signature", {
        variant: "success",
      });
      setCloseNoSigOpen(false);
      navigate("/assignments");
    } catch (err) {
      console.error("[Signatures] Erreur cloture sans signature:", err);
      enqueueSnackbar("Erreur lors de la cloture", { variant: "error" });
    } finally {
      setCloseNoSigLoading(false);
    }
  };

  // Gérer le retour à l'étape précédente
  const handlePreviousStep = async () => {
    if (!id) return;

    try {
      setSubmitting(true);
      console.log(
        "[Signatures] Début du processus de retour à l'étape précédente"
      );

      const updatedAssignment = await AssignmentService.previousStep(id);
      setAssignment(updatedAssignment);

      // Rediriger vers la page précédente (inventaire_documents)
      if (isReturn) {
        navigate(`/inventaire-documents/${id}?return=true`);
      } else {
        navigate(`/inventaire-documents/${id}`);
      }
    } catch (err) {
      console.error(
        "[Signatures] Erreur lors du retour à l'étape précédente:",
        err
      );
      enqueueSnackbar("Erreur lors du retour à l'étape précédente", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100vw",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f9f9f9",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !assignment) {
    return (
      <Box
        sx={{
          width: "100vw",
          minHeight: "100vh",
          backgroundColor: "#f9f9f9",
          py: { xs: 2, md: 3 },
          px: { xs: 1, md: 2 },
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: 1600 }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" color="error">
              {error || "Affectation non trouvée"}
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/assignments")}
              sx={{ mt: 2 }}
            >
              Retour à la liste
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100vw",
        minHeight: "100vh",
        backgroundColor: "#f9f9f9",
        py: { xs: 2, md: 3 },
        px: { xs: 1, md: 2 },
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: 1600 }}>
        <Box sx={{ p: 3, minHeight: "100vh" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              gap: isMobile ? 2 : 0,
              mb: 4,
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/assignments")}
              sx={{ mr: 2 }}
              size={isMobile ? "medium" : "large"}
            >
              Retour
            </Button>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
              }}
            >
              Signatures {isReturn ? "de retour" : "d'entrée"}
            </Typography>
          </Box>

          <Alert severity={isReturn ? "warning" : "info"} sx={{ mb: 3 }}>
            <Typography variant="body1">
              <strong>Important :</strong> Les signatures du gestionnaire et du
              conducteur sont obligatoires pour poursuivre le processus{" "}
              {isReturn ? "de retour" : "d'entrée"}.
            </Typography>
          </Alert>

          <Paper
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 2,
              backgroundImage:
                "linear-gradient(to right bottom, #ffffff, #f9f9f9)",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Information sur l'affectation
              </Typography>
              <Grid container spacing={2}>
                <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "1 / 7" } }}>
                  <Typography variant="body1">
                    <strong>Véhicule:</strong> {assignment.vehiclePlate}
                  </Typography>
                </Grid>
                <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "7 / 13" } }}>
                  <Typography variant="body1">
                    <strong>Chauffeur:</strong> {assignment.driverFirstName}{" "}
                    {assignment.driverLastName}
                  </Typography>
                </Grid>
                <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "1 / 7" } }}>
                  <Typography variant="body1">
                    <strong>Date:</strong>{" "}
                    {new Date(assignment.date).toLocaleDateString("fr-FR")}
                  </Typography>
                </Grid>
                <Grid sx={{ gridColumn: { xs: "1 / 13", sm: "7 / 13" } }}>
                  <Typography variant="body1">
                    <strong>Kilométrage:</strong> {assignment.startMileage} km
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Signatures{" "}
              <Typography component="span" color="error">
                *
              </Typography>
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  fontSize: "0.95rem",
                  "& .MuiAlert-icon": {
                    alignItems: "center",
                  },
                }}
              >
                <Typography variant="body2">
                  <strong>Instructions :</strong> Utilisez votre souris ou votre
                  doigt (sur mobile) pour signer dans les cadres ci-dessous. Les
                  deux signatures sont nécessaires pour finaliser cette étape.
                </Typography>
              </Alert>

              {/* Mode usage interne - skip signatures */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                  borderRadius: 2,
                  bgcolor: usageInterne ? "warning.light" : "grey.100",
                  border: usageInterne ? "2px solid" : "1px solid",
                  borderColor: usageInterne ? "warning.main" : "grey.300",
                  transition: "all 0.3s ease",
                }}
              >
                <SpeedIcon sx={{ mr: 1, color: usageInterne ? "warning.dark" : "grey.600" }} />
                <FormControlLabel
                  control={
                    <Switch
                      checked={usageInterne}
                      onChange={(e) => setUsageInterne(e.target.checked)}
                      color="warning"
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 600 }}>
                      Usage interne (sans signatures)
                    </Typography>
                  }
                />
              </Box>
            </Box>

            {!usageInterne && (
            <Grid container spacing={3}>
              <Grid sx={{ gridColumn: { xs: 12, md: 6 } }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#f8f9fa",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    mb: 3,
                    border: managerSigned
                      ? "2px solid #4caf50"
                      : "1px solid #e0e0e0",
                    transition: "all 0.3s ease",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      fontWeight: 600,
                      color: "#424242",
                    }}
                  >
                    Signature du gestionnaire
                    <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                      *
                    </Typography>
                    {managerSigned && (
                      <Typography
                        component="span"
                        color="success.main"
                        sx={{
                          ml: 1,
                          fontSize: "0.8rem",
                          backgroundColor: "rgba(76,175,80,0.1)",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontWeight: 600,
                        }}
                      >
                        Signé
                      </Typography>
                    )}
                  </Typography>
                  <Box
                    sx={{
                      border: managerSigned
                        ? "2px solid #4caf50"
                        : "1px solid #ccc",
                      height: { xs: 100, sm: 120 }, // Réduit pour signature rapide
                      mb: 2,
                      borderRadius: 2,
                      boxShadow: managerSigned
                        ? "0px 0px 8px rgba(76, 175, 80, 0.3)"
                        : "none",
                      overflow: "hidden",
                      position: "relative",
                      width: "100%",
                      "& .signature-canvas": {
                        width: "100% !important",
                        height: "100% !important",
                        position: "absolute",
                        top: 0,
                        left: 0,
                      },
                      "&:before": !managerSigned
                        ? {
                            content: '""',
                            position: "absolute",
                            bottom: "10px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: "80%",
                            height: "1px",
                            backgroundColor: "#ccc",
                            zIndex: 1,
                          }
                        : {},
                    }}
                  >
                    <SignatureCanvas
                      ref={signaturePadRefs.manager}
                      canvasProps={{
                        className: "signature-canvas",
                        style: {
                          cursor: "crosshair",
                          width: "100%",
                          height: "100%",
                        },
                      }}
                      backgroundColor="#f5f5f5"
                      onEnd={() => handleEndDrawing("manager")}
                      clearOnResize={false}
                    />
                  </Box>
                  <Box
                    sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ClearIcon />}
                      onClick={() => clearSignature("manager")}
                      color="error"
                      sx={{
                        borderRadius: 5,
                        px: 2,
                      }}
                    >
                      Effacer
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handlePreviewSignature("manager")}
                      disabled={!managerSigned}
                      sx={{
                        borderRadius: 5,
                        px: 2,
                      }}
                    >
                      Prévisualiser
                    </Button>
                  </Box>
                </Box>
              </Grid>

              <Grid sx={{ gridColumn: { xs: 12, md: 6 } }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#f8f9fa",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    mb: 3,
                    border: conducteurSigned
                      ? "2px solid #4caf50"
                      : "1px solid #e0e0e0",
                    transition: "all 0.3s ease",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      fontWeight: 600,
                      color: "#424242",
                    }}
                  >
                    Signature du conducteur
                    <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                      *
                    </Typography>
                    {conducteurSigned && (
                      <Typography
                        component="span"
                        color="success.main"
                        sx={{
                          ml: 1,
                          fontSize: "0.8rem",
                          backgroundColor: "rgba(76,175,80,0.1)",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontWeight: 600,
                        }}
                      >
                        Signé
                      </Typography>
                    )}
                  </Typography>
                  <Box
                    sx={{
                      border: conducteurSigned
                        ? "2px solid #4caf50"
                        : "1px solid #ccc",
                      height: { xs: 100, sm: 120 }, // Réduit pour signature rapide
                      mb: 2,
                      borderRadius: 2,
                      boxShadow: conducteurSigned
                        ? "0px 0px 8px rgba(76, 175, 80, 0.3)"
                        : "none",
                      overflow: "hidden",
                      position: "relative",
                      width: "100%",
                      "& .signature-canvas": {
                        width: "100% !important",
                        height: "100% !important",
                        position: "absolute",
                        top: 0,
                        left: 0,
                      },
                      "&:before": !conducteurSigned
                        ? {
                            content: '""',
                            position: "absolute",
                            bottom: "10px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: "80%",
                            height: "1px",
                            backgroundColor: "#ccc",
                            zIndex: 1,
                          }
                        : {},
                    }}
                  >
                    <SignatureCanvas
                      ref={signaturePadRefs.conducteur}
                      canvasProps={{
                        className: "signature-canvas",
                        style: {
                          cursor: "crosshair",
                          width: "100%",
                          height: "100%",
                        },
                      }}
                      backgroundColor="#f5f5f5"
                      onEnd={() => handleEndDrawing("conducteur")}
                      clearOnResize={false}
                    />
                  </Box>
                  <Box
                    sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ClearIcon />}
                      onClick={() => clearSignature("conducteur")}
                      color="error"
                      sx={{
                        borderRadius: 5,
                        px: 2,
                      }}
                    >
                      Effacer
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handlePreviewSignature("conducteur")}
                      disabled={!conducteurSigned}
                      sx={{
                        borderRadius: 5,
                        px: 2,
                      }}
                    >
                      Prévisualiser
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
            )}

            {/* Bouton rapide si usage interne */}
            {usageInterne && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  bgcolor: "grey.50",
                  borderRadius: 2,
                  border: "2px dashed",
                  borderColor: "grey.300",
                }}
              >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Mode usage interne activé
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Les signatures seront ignorées pour cette affectation.
                </Typography>
              </Box>
            )}
          </Paper>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 2 : 0,
              alignItems: isMobile ? "stretch" : "center",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate("/assignments")}
              fullWidth={isMobile}
              sx={{
                borderRadius: 2,
              }}
            >
              Annuler
            </Button>
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                gap: 2,
              }}
            >
              {!areSignaturesComplete() && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{
                    fontWeight: "bold",
                    p: 1,
                    border: "1px solid rgba(211, 47, 47, 0.3)",
                    borderRadius: 1,
                    backgroundColor: "rgba(211, 47, 47, 0.08)",
                    textAlign: isMobile ? "center" : "left",
                  }}
                >
                  Les deux signatures sont requises
                </Typography>
              )}
              {isReturn && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<BlockIcon />}
                  onClick={() => setCloseNoSigOpen(true)}
                  disabled={submitting}
                  fullWidth={isMobile}
                  sx={{ borderRadius: 2 }}
                >
                  Cloturer sans signature
                </Button>
              )}
              <Button
                variant="outlined"
                color="secondary"
                onClick={handlePreviousStep}
                disabled={submitting}
                fullWidth={isMobile}
                sx={{
                  borderRadius: 2,
                }}
              >
                Etape precedente
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={submitting || !areSignaturesComplete()}
                fullWidth={isMobile}
                sx={{
                  borderRadius: 2,
                  backgroundImage:
                    "linear-gradient(to right, #3f51b5, #5c6bc0)",
                  boxShadow: "0 4px 10px rgba(63, 81, 181, 0.2)",
                  "&:hover": {
                    backgroundImage:
                      "linear-gradient(to right, #303f9f, #3f51b5)",
                    boxShadow: "0 6px 12px rgba(63, 81, 181, 0.3)",
                  },
                }}
              >
                {submitting ? "Enregistrement..." : "Finaliser les signatures"}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Dialog de prévisualisation des signatures */}
        <Dialog
          open={previewState.open}
          onClose={handleClosePreview}
          maxWidth="sm"
          fullWidth
          disableEnforceFocus={false}
          disableAutoFocus={false}
          disableRestoreFocus={false}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
              fontWeight: 600,
            }}
          >
            {previewState.title}
          </DialogTitle>
          <DialogContent sx={{ p: 3, mt: 2 }}>
            {previewState.signature && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  p: 2,
                  border: "1px solid #ccc",
                  borderRadius: 2,
                  backgroundColor: "#f5f5f5",
                  boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <img
                  src={previewState.signature}
                  alt="Signature"
                  style={{ maxWidth: "100%" }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              p: 2,
              borderTop: "1px solid #e0e0e0",
              justifyContent: "space-between",
            }}
          >
            <Button
              onClick={handleClosePreview}
              variant="contained"
              sx={{
                borderRadius: 5,
                px: 3,
              }}
            >
              Fermer
            </Button>
          </DialogActions>
        </Dialog>

        {/* #50 — Dialog clôturer sans signature */}
        <Dialog
          open={closeNoSigOpen}
          onClose={() => setCloseNoSigOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: "#fef2f2",
              borderBottom: "1px solid #fecaca",
              fontWeight: 600,
              color: "#dc2626",
            }}
          >
            Cloturer sans signature
          </DialogTitle>
          <DialogContent sx={{ p: 3, mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Attention :</strong> Cette action terminera
                l'affectation sans recueillir les signatures de retour.
                Un email sera envoye au chauffeur si possible.
              </Typography>
            </Alert>
            <FormControl fullWidth>
              <InputLabel id="close-reason-label">Motif</InputLabel>
              <Select
                labelId="close-reason-label"
                value={closeNoSigReason}
                label="Motif"
                onChange={(e) => setCloseNoSigReason(e.target.value)}
              >
                <MenuItem value="refus_signature">
                  Refus de signature
                </MenuItem>
                <MenuItem value="depot_nocturne">Depot nocturne</MenuItem>
                <MenuItem value="depot_garagiste">
                  Depot chez un garagiste
                </MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions
            sx={{
              p: 2,
              borderTop: "1px solid #e0e0e0",
              justifyContent: "space-between",
            }}
          >
            <Button
              onClick={() => setCloseNoSigOpen(false)}
              variant="outlined"
              sx={{ borderRadius: 5, px: 3 }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleCloseWithoutSignature}
              color="error"
              variant="contained"
              disabled={!closeNoSigReason || closeNoSigLoading}
              sx={{ borderRadius: 5, px: 3 }}
            >
              {closeNoSigLoading
                ? "Cloture en cours..."
                : "Confirmer la cloture"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmation avant soumission */}
        <Dialog
          open={confirmDialogOpen}
          onClose={handleCancelSubmit}
          maxWidth="sm"
          fullWidth
          disableEnforceFocus={false}
          disableAutoFocus={false}
          disableRestoreFocus={false}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: "#f5f5f5",
              borderBottom: "1px solid #e0e0e0",
              fontWeight: 600,
            }}
          >
            Confirmer les signatures
          </DialogTitle>
          <DialogContent sx={{ p: 3, mt: 2 }}>
            <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
              <Typography variant="body1">
                <strong>Important :</strong> Une fois les signatures
                enregistrées, elles ne pourront plus être modifiées.
              </Typography>
            </Alert>
            <Typography variant="body1">
              Vous êtes sur le point de finaliser les signatures{" "}
              {isReturn ? "de retour" : "d'entrée"} pour ce véhicule. Êtes-vous
              sûr de vouloir continuer?
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              p: 2,
              borderTop: "1px solid #e0e0e0",
              justifyContent: "space-between",
            }}
          >
            <Button
              onClick={handleCancelSubmit}
              color="secondary"
              variant="outlined"
              sx={{
                borderRadius: 5,
                px: 3,
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleConfirmSubmit}
              color="primary"
              variant="contained"
              disabled={submitting}
              sx={{
                borderRadius: 5,
                px: 3,
                backgroundImage: "linear-gradient(to right, #3f51b5, #5c6bc0)",
                boxShadow: "0 4px 10px rgba(63, 81, 181, 0.2)",
                "&:hover": {
                  backgroundImage:
                    "linear-gradient(to right, #303f9f, #3f51b5)",
                  boxShadow: "0 6px 12px rgba(63, 81, 181, 0.3)",
                },
              }}
            >
              {submitting
                ? "Traitement en cours..."
                : "Confirmer et enregistrer"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Signatures;
